import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Leaf, Sprout, Scissors, Grid3x3, TreePine, Mountain, Square, Star, ArrowUpRight, Instagram, Facebook } from "lucide-react";
import { api } from "@/lib/api";
import { useSettings } from "@/context/AppContext";
import PublicLayout from "@/components/PublicLayout";
import { readCache, writeCache } from "@/lib/cache";
import SmartImage from "@/components/SmartImage";
import ImageSkeleton from "@/components/ImageSkeleton";
import {
  sizeHeroTitle,
  sizeHeroSubtitle,
  sizeHeroLabel,
  sizeSectionTitle,
  sizeSectionText,
} from "@/lib/textSize";

const ICONS = { Leaf, Sprout, Scissors, Grid3x3, TreePine, Mountain, Square, Sparkles };

const CATEGORIES = [
  { id: "all", label: "Tout" },
  { id: "jardins-prives", label: "Jardins privés" },
  { id: "entretien", label: "Entretien" },
  { id: "plantations", label: "Plantations" },
  { id: "amenagements", label: "Aménagements" },
];

export default function HomePage() {
  const { settings, fresh } = useSettings();
  const cachedServices = readCache("services", []);
  const cachedReals = readCache("realizations", []);
  const [services, setServices] = useState(cachedServices);
  const [realisations, setRealisations] = useState(cachedReals);
  const [servicesFresh, setServicesFresh] = useState(false);
  const [realsFresh, setRealsFresh] = useState(false);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/services").then((r) => {
      const data = r.data || [];
      setServices(data);
      writeCache("services", data);
      setServicesFresh(true);
    });
    api.get("/realizations?summary=true").then((r) => {
      const data = r.data || [];
      setRealisations(data);
      writeCache("realizations", data);
      setRealsFresh(true);
    });
  }, []);

  const visibleReal = filter === "all" ? realisations : realisations.filter((r) => r.category === filter);

  // Tailles dynamiques (configurables depuis l'admin)
  const heroTitleStyle = { fontSize: sizeHeroTitle(settings?.hero_title_size) };
  const heroSubtitleStyle = { fontSize: sizeHeroSubtitle(settings?.hero_subtitle_size) };
  const heroLabelStyle = { fontSize: sizeHeroLabel(settings?.hero_label_size) };
  const sectionTitleStyle = { fontSize: sizeSectionTitle(settings?.section_title_size) };
  const sectionTextStyle = { fontSize: sizeSectionText(settings?.section_text_size) };

  return (
    <PublicLayout>
      {/* HERO */}
      <section className="relative min-h-screen flex items-end overflow-hidden bg-forest-deep" data-testid="hero-section">
        <div className="absolute inset-0">
          {fresh ? (
            settings?.hero_image ? (
              <SmartImage
                src={settings.hero_image}
                alt="Jardin paysager"
                className="absolute inset-0 w-full h-full"
                width={1920}
                eager
              />
            ) : null
          ) : (
            <ImageSkeleton dark className="absolute inset-0 w-full h-full" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-forest/80 via-forest/30 to-transparent" />
        </div>
        <div className="relative container-px max-w-7xl mx-auto pb-20 md:pb-28 pt-32 grid grid-cols-1 md:grid-cols-12 gap-8 w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-9 lg:col-span-8"
          >
            <p className="label-eyebrow text-cream/80 mb-6" style={heroLabelStyle}>Pizzelli Paysagiste · Moudon, Vaud</p>
            <h1
              data-testid="hero-title"
              className="font-display text-cream leading-[0.95] tracking-tight font-light"
              style={heroTitleStyle}
            >
              {settings?.hero_title}
            </h1>
            <p className="mt-8 max-w-xl text-cream/85 leading-relaxed font-light" style={heroSubtitleStyle}>
              {settings?.hero_subtitle}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contact" className="btn-corten" data-testid="hero-cta-quote">
                {settings?.cta_label || "Demander un devis"} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/realisations" className="btn-secondary !text-cream !border-cream/40 hover:!bg-cream hover:!text-forest" data-testid="hero-cta-realisations">
                Voir nos réalisations
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="py-6 bg-bone border-y border-sand overflow-hidden">
        <div className="flex animate-marquee whitespace-nowrap">
          {[...Array(2)].map((_, k) => (
            <div key={k} className="flex items-center gap-12 pr-12">
              {["Création de jardins", "Entretien", "Taille de haies", "Plantations", "Aménagements", "Terrasses", "Gazon", "Nettoyage"].map((w, i) => (
                <span key={i} className="font-display italic text-2xl text-forest/60">
                  {w} <span className="mx-6 text-corten">✦</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* ABOUT */}
      <section className="py-24 md:py-36 bg-background" data-testid="about-section">
        <div className="container-px max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="md:col-span-6 relative">
            <div className="aspect-[4/5] overflow-hidden">
              <SmartImage
                src={settings?.about_image}
                alt="Notre savoir-faire"
                className="w-full h-full hover:scale-105 transition duration-1000"
                width={900}
              />
            </div>
            <div className="absolute -bottom-6 -right-6 bg-corten text-cream p-8 max-w-[220px] hidden md:block">
              <Sparkles className="w-5 h-5 mb-3" />
              <p className="font-display text-2xl leading-tight">Travail soigné, durable, passionné.</p>
            </div>
          </div>
          <div className="md:col-span-6">
            <p className="label-eyebrow mb-5">À propos</p>
            <h2 className="font-display text-forest font-light tracking-tight leading-[1.05]" style={sectionTitleStyle}>
              {settings?.about_title}
            </h2>
            <p className="mt-8 text-forest-soft leading-relaxed max-w-lg" style={sectionTextStyle}>
              {settings?.about_text}
            </p>
            <ul className="mt-10 grid grid-cols-2 gap-3 max-w-md">
              {(settings?.about_values || []).map((v, i) => (
                <li key={i} className="flex items-center gap-3 text-sm text-forest">
                  <Star className="w-3.5 h-3.5 text-corten fill-corten" />
                  {v}
                </li>
              ))}
            </ul>
            <Link to="/a-propos" className="inline-flex items-center gap-2 mt-12 text-forest link-underline text-sm">
              En savoir plus sur l'entreprise <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-24 md:py-36 bg-bone" data-testid="services-section">
        <div className="container-px max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <div>
              <p className="label-eyebrow mb-5">Nos services</p>
              <h2 className="font-display text-forest font-light tracking-tight leading-[1.05] max-w-2xl" style={sectionTitleStyle}>
                Tout pour un jardin qui vous ressemble.
              </h2>
            </div>
            <Link to="/services" className="text-forest link-underline text-sm self-start md:self-auto">
              Tous les services →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {!servicesFresh
              ? Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="bg-cream border border-sand overflow-hidden">
                    <ImageSkeleton className="aspect-[4/3] w-full" />
                    <div className="p-6">
                      <div className="w-5 h-5 bg-sand mb-4" />
                      <div className="h-5 bg-sand w-3/4 mb-3" />
                      <div className="h-3 bg-sand w-full mb-1.5" />
                      <div className="h-3 bg-sand w-5/6" />
                    </div>
                  </div>
                ))
              : services.slice(0, 8).map((s, i) => {
              const Icon = ICONS[s.icon] || Leaf;
              return (
                <motion.div
                  key={s.id}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: (i % 4) * 0.05 }}
                  data-testid={`service-card-${i}`}
                  className="group bg-cream border border-sand overflow-hidden hover:-translate-y-1 transition duration-500"
                >
                  <div className="aspect-[4/3] overflow-hidden bg-forest/10">
                    {s.image && (
                      <SmartImage
                        src={s.image}
                        alt={s.title}
                        className="w-full h-full group-hover:scale-110 transition duration-1000"
                        width={800}
                      />
                    )}
                  </div>
                  <div className="p-6">
                    <Icon className="w-5 h-5 text-corten mb-4" strokeWidth={1.5} />
                    <h3 className="font-display text-xl text-forest leading-snug">{s.title}</h3>
                    <p className="mt-3 text-sm text-forest-soft leading-relaxed line-clamp-3">{s.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* REALISATIONS */}
      <section className="py-24 md:py-36 bg-background" data-testid="realisations-section">
        <div className="container-px max-w-7xl mx-auto">
          <div className="mb-16">
            <p className="label-eyebrow mb-5">Réalisations</p>
            <h2 className="font-display text-forest font-light tracking-tight leading-[1.05] max-w-2xl" style={sectionTitleStyle}>
              Des projets pensés, des jardins vivants.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-5">
            {!realsFresh
              ? Array.from({ length: 6 }).map((_, i) => (
                  <ImageSkeleton key={i} className={`${i === 0 ? "lg:row-span-2 lg:aspect-auto aspect-[4/5]" : "aspect-[4/5]"}`} />
                ))
              : visibleReal.slice(0, 6).map((r, i) => (
              <motion.div
                key={r.id}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: (i % 3) * 0.05 }}
                className={`group relative overflow-hidden ${i === 0 ? "lg:row-span-2 lg:aspect-auto aspect-[4/5]" : "aspect-[4/5]"}`}
                data-testid={`realisation-card-${i}`}
              >
                <img
                  src={r.cover}
                  alt={r.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition duration-1000"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-forest/90 via-forest/20 to-transparent" />
                <div className="absolute bottom-0 left-0 p-6 text-cream">
                  <p className="text-[10px] uppercase tracking-[0.22em] opacity-80">
                    {CATEGORIES.find((c) => c.id === r.category)?.label || r.category}
                  </p>
                  <h3 className="font-display text-2xl mt-2 leading-tight">{r.title}</h3>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Link to="/realisations" className="btn-secondary">Toutes nos réalisations</Link>
          </div>
        </div>
      </section>

      {/* TARGET AUDIENCE */}
      <section className="py-24 md:py-36 bg-forest text-cream" data-testid="audience-section">
        <div className="container-px max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-px bg-cream/10">
          <div className="bg-forest p-10 md:p-16">
            <p className="label-eyebrow text-cream/60 mb-5">Pour qui ?</p>
            <h3 className="font-display text-3xl md:text-4xl lg:text-5xl font-light leading-tight">
              {settings?.targets?.particuliers_title}
            </h3>
            <p className="mt-6 text-cream/75 leading-relaxed">{settings?.targets?.particuliers_text}</p>
            <Link to="/contact" className="inline-flex items-center gap-2 mt-10 text-cream link-underline text-sm">
              Discuter de mon projet <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="bg-forest-deep p-10 md:p-16">
            <p className="label-eyebrow text-cream/60 mb-5">Pour qui ?</p>
            <h3 className="font-display text-3xl md:text-4xl lg:text-5xl font-light leading-tight">
              {settings?.targets?.professionnels_title}
            </h3>
            <p className="mt-6 text-cream/75 leading-relaxed">{settings?.targets?.professionnels_text}</p>
            <Link to="/contact" className="inline-flex items-center gap-2 mt-10 text-cream link-underline text-sm">
              Demander une offre <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* SOCIAL */}
      <section className="py-24 md:py-32 bg-bone">
        <div className="container-px max-w-5xl mx-auto text-center">
          <p className="label-eyebrow mb-5">Suivez-nous</p>
          <h2 className="font-display text-4xl md:text-5xl text-forest font-light tracking-tight">
            Découvrez nos chantiers en cours sur les réseaux.
          </h2>
          <div className="mt-10 flex items-center justify-center gap-4">
            {settings?.instagram_url && (
              <a href={settings.instagram_url} target="_blank" rel="noreferrer" className="btn-secondary" data-testid="social-instagram">
                <Instagram className="w-4 h-4" /> Instagram
              </a>
            )}
            {settings?.facebook_url && (
              <a href={settings.facebook_url} target="_blank" rel="noreferrer" className="btn-secondary" data-testid="social-facebook">
                <Facebook className="w-4 h-4" /> Facebook
              </a>
            )}
            {!settings?.instagram_url && !settings?.facebook_url && (
              <p className="text-forest-soft text-sm">Liens à venir.</p>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 md:py-36 bg-corten text-cream overflow-hidden">
        <div className="container-px max-w-5xl mx-auto text-center">
          <h2 className="font-display text-4xl md:text-6xl font-light leading-[1.05]">
            Et si nous concevions votre jardin ?
          </h2>
          <p className="mt-6 max-w-xl mx-auto text-cream/80">
            Échangeons sur votre projet et recevez un devis personnalisé.
          </p>
          <Link to="/contact" className="inline-flex items-center gap-2 mt-10 px-8 py-4 bg-cream text-forest font-medium text-sm tracking-wide hover:-translate-y-[1px] transition" data-testid="bottom-cta">
            {settings?.cta_label || "Demander un devis"} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </PublicLayout>
  );
}
